import React, { useEffect, useMemo, useState } from 'react';
import { prop } from 'ramda';

import { Form } from '@core/js/components/common/Form';

import { Button } from '@interlayer/js/app/components/common/Button';
import { Select } from '@interlayer/js/components/common/Fields/Select/Select';
import { WebContent } from '@interlayer/js/components/system/WebContent/WebContent';

import { useTranslations } from '@core/js/hooks/i18n';
import { setPlayerTags } from '@core/js/hooks/user/server';
import { useUserSelector } from '@core/js/store/hooks';

import { STAMPER_CARD_TYPES } from '@interlayer/js/app/components/main/StamperCard/constants';
import { TStamperCardProps } from '@interlayer/js/app/components/main/StamperCard/types';
import { useAllPlayerTags } from '@interlayer/js/app/hooks/user/hooks/useAllPlayerTags';
import { clsx } from '@interlayer/js/app/utils/clsx.util';

import style from './StamperCard.scss';

export const StamperCard: React.FC<TStamperCardProps> = ({ config: { cards, defaultInfoWC } }) => {
    const t = useTranslations('playtech.mobile.system.StamperCard.');
    const { fetch: fetchAllPlayerTags } = useAllPlayerTags({ isInitFetch: false, isForceFetch: true });
    const playerTags = useUserSelector(prop('allPlayerTags'));
    const [activeCardsType, setActiveCardsType] = useState(STAMPER_CARD_TYPES.active);
    const activeCards = useMemo(() => {
        return cards.filter((card) => {
            const tag = playerTags?.[card.playerTag];

            if (!tag) {
                return false;
            }

            return activeCardsType === STAMPER_CARD_TYPES.active
                ? Number(tag.value) !== 99
                : Number(tag.value) === 99;
        });
    }, [activeCardsType, cards, playerTags]);
    const [activeCard, setActiveCard] = useState({ index: 0, card: activeCards[0] });

    const activeStamps = useMemo(() => {
        return Number(playerTags?.[activeCard?.card?.playerTag]?.value || 0);
    }, [activeCard?.card?.playerTag, playerTags]);

    const options = useMemo(() => Object.values(STAMPER_CARD_TYPES).map((type) => ({
        value: type, label: t(`type.${type}`)
    })), [t]);

    const onChangeActiveCardsType = (e: React.ChangeEvent<HTMLInputElement>) => {
        setActiveCardsType(
            e.target.value === STAMPER_CARD_TYPES.active
                ? STAMPER_CARD_TYPES.active
                : STAMPER_CARD_TYPES.expired
        );
    };

    const handleLeftClick = () => {
        setActiveCard({ index: activeCard.index - 1, card: activeCards[activeCard.index - 1] });
    };

    const handleRightClick = () => {
        setActiveCard({ index: activeCard.index + 1, card: activeCards[activeCard.index + 1] });
    };

    const handleBulletClick = (i: number) => () => {
        setActiveCard({ index: i, card: activeCards[i] });
    };

    const handleRedeemClick = () => {
        setPlayerTags([{
            name: activeCard.card.playerTag,
            type: playerTags?.[activeCard.card.playerTag]?.type || 'text',
            value: 99
        }])
            .then(() => {
                fetchAllPlayerTags();
            });
    };

    useEffect(() => {
        setActiveCard({ index: 0, card: activeCards[0] });
    }, [activeCardsType, activeCards]);

    if (!cards.length) {
        return null;
    }

    return (
        <div className={style.container}>
            <div className={style.wrapper}>
                {Boolean(activeCard?.card?.infoWC || defaultInfoWC) && (
                    <WebContent id={activeCard?.card?.infoWC || defaultInfoWC} className={style.infoWC} />
                )}

                <div className={style.cardWrapper}>
                    <Form
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                        render={() => (
                            <form>
                                <Select
                                    name="activeCardType"
                                    options={options}
                                    value={activeCardsType}
                                    className={style.select}
                                    onChange={onChangeActiveCardsType}
                                />
                            </form>
                        )}
                    />

                    {activeCards.length
                        ? (
                            <>
                                <div className={style.titleWrapper}>
                                    <span
                                        onClick={handleLeftClick}
                                        className={clsx(style.triangle, style.left, { [style.disabled]: activeCard.index === 0 })}
                                    />

                                    <span className={style.title}>{activeCard.card?.title}</span>

                                    <span
                                        onClick={handleRightClick}
                                        className={clsx(style.triangle, style.right, {
                                            [style.disabled]: activeCard.index === activeCards.length - 1
                                        })}
                                    />
                                </div>

                                <div
                                    className={clsx(style.stampsWrapper, {
                                        [style.canRedeem]: activeStamps >= activeCard.card?.maxStamps || activeStamps === 99
                                    })}
                                >
                                    {Array.from({ length: activeCard.card?.maxStamps || 0 }).map((_, i) => (
                                        <div key={i} className={style.stampBlock}>
                                            {i + 1 <= activeStamps && (
                                                <img
                                                    alt="stamp"
                                                    src={activeCard.card?.stampImage}
                                                    className={style.stampImg}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className={style.bulletsWrapper}>
                                    {Array.from({ length: activeCards.length }).map((_, i) => (
                                        <span
                                            key={i}
                                            onClick={handleBulletClick(i)}
                                            className={clsx(style.bullet, { [style.active]: i === activeCard.index })}
                                        />
                                    ))}
                                </div>

                                <Button
                                    className={style.btn}
                                    onClick={handleRedeemClick}
                                    disabled={!(activeStamps === activeCard.card?.maxStamps)}
                                >
                                    {t('button.submit')}
                                </Button>
                            </>
                        )
                        : (
                            <span className={style.noCards}>{t(`empty.${activeCardsType}`)}</span>
                        )
                    }
                </div>
            </div>

            {activeCard?.card?.bottomWC && (
                <WebContent id={activeCard.card.bottomWC} className={style.bottomWC} />
            )}
        </div>
    );
};

export default StamperCard;
